import React from "react";
import { createBrowserRouter, RouterProvider, ScrollRestoration, Outlet } from "react-router";
import { Theme } from "@gusarov-studio/rubik-ui";
import { routesMap } from "./routesMap";
import { ErrorFallback } from "../common/components/ErrorBoundary";
import { SignupScreen } from "../modules/auth/screens/SignupScreen";
import { LoginScreen } from "../modules/auth/screens/LoginScreen";
import { LogoutScreen } from "../modules/auth/screens/LogoutScreen";
import { LoginWithEmailScreen } from "../modules/auth/screens/LoginWithEmailScreen";
import { SignupWithEmailScreen } from "../modules/auth/screens/SignupWithEmailScreen";
import { authenticationGateway } from "../modules/auth/helpers/authenticationGateway";

const router = createBrowserRouter([
  {
    // Root layout to include ScrollRestoration
    element: (
      <>
        <ScrollRestoration />
        <Outlet />
      </>
    ),
    errorElement: <ErrorFallback />,
    children: [
      {
        element: (
          <Theme
            value="r-theme-light"
            className="h-full"
          >
            <Outlet />
          </Theme>
        ),
        children: [
          {
            path: routesMap.home.path,
            lazy: async () => {
              const { HomePage } = await import("../pages/home");
              return { element: <HomePage /> };
            },
          },
          {
            path: routesMap.pricing.path,
            lazy: async () => {
              const { PricingPage } = await import("../pages/pricing");
              return { element: <PricingPage /> };
            },
          },
          {
            path: routesMap.changelog.path,
            loader: async () => {
              const { fetchPost, getAllPostIds } = await import("../pages/changelog/context");
              const postsIds = getAllPostIds();
              const posts = await Promise.all(
                postsIds.map(async (postId) => {
                  const { metadata } = await fetchPost(postId);
                  return { id: postId, ...metadata };
                }),
              );
              return { posts };
            },
            lazy: async () => {
              const { ChangelogPage } = await import("../pages/changelog");
              return { element: <ChangelogPage /> };
            },
          },
          {
            path: routesMap.changelogPost.path,
            loader: async ({ params }) => {
              const { postId } = params;
              const { fetchPost } = await import("../pages/changelog/context");
              const { MDXPost, metadata } = await fetchPost(postId!);
              return { MDXPost, metadata, postId };
            },
            lazy: async () => {
              const { ChangelogUpdatePage } = await import("../pages/changelog/post");
              return { element: <ChangelogUpdatePage /> };
            },
          },
          {
            path: routesMap.models.path,
            loader: async () => {
              const { fetchListing } = await import("../pages/models/context");
              const listingId = "openai-models";
              const listingData = await fetchListing(listingId);
              return { listingData, listingId };
            },
            lazy: async () => {
              const { ModelsPage } = await import("../pages/models");
              return { element: <ModelsPage /> };
            },
          },
          {
            path: routesMap.modelPost.path,
            loader: async ({ params }) => {
              const { modelId } = params;
              const { fetchModel } = await import("../pages/models/context");
              const modelData = await fetchModel(modelId!);
              return { modelData, modelId };
            },
            lazy: async () => {
              const { ModelDetailPage } = await import("../pages/models/spec");
              return { element: <ModelDetailPage /> };
            },
          },
          {
            path: routesMap.privacy.path,
            lazy: async () => {
              const { PrivacyPage } = await import("../pages/privacy");
              return { element: <PrivacyPage /> };
            },
          },
          {
            path: routesMap.terms.path,
            lazy: async () => {
              const { TermsPage } = await import("../pages/terms");
              return { element: <TermsPage /> };
            },
          },
          {
            path: routesMap.login.path,
            element: <LoginScreen />,
          },
          {
            path: routesMap.signup.path,
            element: <SignupScreen />,
          },
          {
            path: routesMap.loginWithEmail.path,
            element: <LoginWithEmailScreen />,
          },
          {
            path: routesMap.signupWithEmail.path,
            element: <SignupWithEmailScreen />,
          },
          {
            path: routesMap.logout.path,
            element: <LogoutScreen />,
          },
          {
            path: routesMap.onboarding.path,
            loader: authenticationGateway,
            lazy: async () => {
              const { OnboardingScreen } = await import(
                "../modules/onboarding/screens/OnboardingScreen"
              );
              return { element: <OnboardingScreen /> };
            },
          },
        ],
      },
      {
        path: routesMap.home.path,
        lazy: async () => {
          const { App } = await import("../app/App");
          return { element: <App /> };
        },
        children: [
          {
            path: routesMap.editor.path,
            lazy: async () => {
              const { EditorScreen } = await import("../modules/editor/screens/EditorScreen");
              return { element: <EditorScreen /> };
            },
            children: [
              {
                path: routesMap.embedPlayground.path,
                lazy: async () => {
                  const { PlaygroundEmbedScreen } = await import(
                    "../modules/playground/screens/PlaygroundEmbedScreen"
                  );
                  return {
                    element: (
                      <Theme
                        value="r-theme-light"
                        className="h-full"
                      >
                        <PlaygroundEmbedScreen />
                      </Theme>
                    ),
                  };
                },
              },
              {
                path: routesMap.prompts.path,
                lazy: async () => {
                  const { EditPromptScreen } = await import(
                    "../modules/prompts/screens/EditPromptScreen"
                  );
                  return { element: <EditPromptScreen /> };
                },
              },
              {
                path: routesMap.chats.path,
                loader: authenticationGateway,
                lazy: async () => {
                  const { ChatsScreen } = await import("../modules/chats/screens/ChatsScreen");
                  return { element: <ChatsScreen /> };
                },
              },
              {
                path: routesMap.evals.path,
                // No authenticationGateway — shared "anyone with the link" evals must be
                // viewable anonymously, same as prompts.
                lazy: async () => {
                  const { EditEvalScreen } = await import(
                    "../modules/evals/screens/EditEvalScreen"
                  );
                  return { element: <EditEvalScreen /> };
                },
              },
              {
                path: routesMap.newFile.path,
                loader: authenticationGateway,
                lazy: async () => {
                  const { NewFileScreen } = await import("../modules/editor/screens/NewFileScreen");
                  return { element: <NewFileScreen /> };
                },
              },
              {
                path: routesMap.upgrade.path,
                loader: authenticationGateway,
                lazy: async () => {
                  const { PricingScreen } = await import(
                    "../modules/payments/screens/PricingScreen"
                  );
                  return { element: <PricingScreen /> };
                },
              },
              {
                path: routesMap.settings.path,
                loader: authenticationGateway,
                lazy: async () => {
                  const { SettingsScreen } = await import(
                    "../modules/users/screens/SettingsScreen"
                  );
                  return { element: <SettingsScreen /> };
                },
              },
            ],
          },
        ],
      },
    ],
  },
]);

function Router() {
  return <RouterProvider router={router} />;
}

export { Router };
